import { Machine, assign } from 'xstate';
import matchSorter from 'match-sorter';

import { logDetails } from '.';

const saveGuard = (
    context: DropdownInputContext,
    event: DropdownInputEvent,
): boolean => {
    if (event.type !== 'SAVE') {
        return false;
    }

    const optionId = event?.value?.optionId;

    return optionId !== undefined && !context.disabledOptionIds.has(optionId);
};

export const dropdownInputMachine = Machine<
    DropdownInputContext,
    // DropdownInputStateSchema,
    DropdownInputEvent
>(
    {
        id: 'dropdownInput',
        initial: 'closed',
        context: {
            didOpen: false,
            disabledOptionIds: new Set(),
            filteredOptions: [],
            options: null,
            optionLists: null,
            selectedId: null,
            highlightedId: undefined,
        },
        states: {
            closed: {
                on: {
                    START_EDITING: 'open',
                    SET_OPTIONS: {
                        actions: ['setOptions'],
                    },
                    SET_OPTION_LISTS: {
                        actions: ['setOptionLists'],
                    },
                    SET_DISABLED_OPTION_IDS: {
                        actions: ['setDisabledOptionIds'],
                    },
                    SET_DEFAULT: {
                        actions: ['setDefault'],
                    },
                    SAVE: {
                        cond: saveGuard,
                        actions: ['save'],
                    },
                },
            },
            open: {
                entry: ['setDidOpen'],
                on: {
                    HIGHLIGHT: {
                        actions: ['highlight'],
                    },
                    SAVE: {
                        target: 'closed',
                        cond: saveGuard,
                        actions: ['save'],
                    },
                    CANCEL: {
                        target: 'closed',
                        actions: ['cancel'],
                    },
                    INPUT: {
                        actions: ['input'],
                    },
                    ARROW_UP: {
                        actions: ['arrowUp'],
                    },
                    ARROW_DOWN: {
                        actions: ['arrowDown'],
                    },
                    SET_DEFAULT: {
                        actions: ['setDefault'],
                        target: 'closed',
                    },
                },
            },
        },
    },
    {
        actions: {
            logDetails,
            setDidOpen: assign(
                (context): DropdownInputContext => ({
                    ...context,
                    didOpen: true,
                }),
            ),
            setOptions: assign((context, event): DropdownInputContext => {
                if (event.type !== 'SET_OPTIONS') {
                    return context;
                }

                return {
                    ...context,
                    filteredOptions: event.value,
                    options: event.value,
                    optionLists: null,
                };
            }),
            setOptionLists: assign((context, event): DropdownInputContext => {
                if (event.type !== 'SET_OPTION_LISTS') {
                    return context;
                }

                const nextFilteredOptions = event.value.flat(1);

                return {
                    ...context,
                    filteredOptions: nextFilteredOptions,
                    optionLists: event.value,
                    options: null,
                };
            }),
            setDisabledOptionIds: assign(
                (context, event): DropdownInputContext => {
                    if (event.type !== 'SET_DISABLED_OPTION_IDS') {
                        return context;
                    }

                    return {
                        ...context,
                        disabledOptionIds: event.value.disabledOptionIds,
                    };
                },
            ),
            setDefault: assign((context, event): DropdownInputContext => {
                if (event.type !== 'SET_DEFAULT') {
                    return context;
                }

                return {
                    ...context,
                    selectedId: event.value.optionId,
                };
            }),
            highlight: assign((context, event): DropdownInputContext => {
                if (event.type !== 'HIGHLIGHT') {
                    return context;
                }

                const optionId = event?.value?.optionId;

                return {
                    ...context,
                    highlightedId: optionId,
                };
            }),
            save: assign((context, event): DropdownInputContext => {
                if (event.type !== 'SAVE') {
                    return context;
                }

                const {
                    value: { optionId, handleChange },
                } = event;
                const { filteredOptions, options, optionLists, selectedId } =
                    context;

                let prevOption = null;
                let nextFilteredOptions: DropdownInputOption[] = [];

                if (options) {
                    prevOption =
                        options.find(
                            ({ id }: DropdownInputOption): boolean =>
                                id === selectedId,
                        ) ?? null;
                    nextFilteredOptions = options;
                } else if (optionLists) {
                    prevOption =
                        optionLists
                            .flat(1)
                            .find(
                                ({ id }: DropdownInputOption): boolean =>
                                    id === selectedId,
                            ) ?? null;
                    nextFilteredOptions = optionLists.flat(1);
                }

                const option =
                    filteredOptions.find(
                        ({ id }: DropdownInputOption): boolean =>
                            id === optionId,
                    ) ?? null;

                handleChange(prevOption, option);

                return {
                    ...context,
                    highlightedId: undefined,
                    selectedId: optionId,
                    filteredOptions: nextFilteredOptions,
                };
            }),
            cancel: assign((context, event): DropdownInputContext => {
                if (event.type !== 'CANCEL') {
                    return context;
                }

                const { options, optionLists } = context;

                let nextFilteredOptions: DropdownInputOption[] = [];

                if (options) {
                    nextFilteredOptions = options;
                } else if (optionLists) {
                    nextFilteredOptions = optionLists.flat(1);
                }

                return {
                    ...context,
                    highlightedId: undefined,
                    filteredOptions: nextFilteredOptions,
                };
            }),
            input: assign((context, event): DropdownInputContext => {
                if (event.type !== 'INPUT') {
                    return context;
                }

                // a DropdownInput can either be given a single array of options, which
                // is stored in context.options, or an array of one or more arrays of
                // options, which are stored in context.optionLists.

                // If context.options exists, filter it by the input
                if (context.options) {
                    const filteredOptions = matchSorter(
                        context?.options ?? [],
                        event.value,
                        {
                            keys: ['label'],
                            threshold: matchSorter.rankings.CONTAINS,
                        },
                    );

                    // If there is at least one filtered option, highlight the first filtered
                    // option by default
                    const highlightedId = filteredOptions?.[0]?.id;

                    return { ...context, filteredOptions, highlightedId };
                }

                // Otherwise, if context.filteredOptionLists exists, filter each list by
                // the input, then concatenate them into the next filteredOptions list
                if (context.optionLists) {
                    const filteredOptionLists = context.optionLists.map(
                        (
                            optionList: DropdownInputOption[],
                        ): DropdownInputOption[] =>
                            matchSorter(optionList, event.value, {
                                keys: ['label'],
                                threshold: matchSorter.rankings.CONTAINS,
                            }),
                    );

                    const filteredOptions = filteredOptionLists.flat(1);

                    // If there is at least one filtered option, highlight the first filtered
                    // option by default
                    const highlightedId = filteredOptions?.[0]?.id;

                    return { ...context, filteredOptions, highlightedId };
                }

                return context;
            }),
            arrowUp: assign((context, event): DropdownInputContext => {
                if (event.type !== 'ARROW_UP') {
                    return context;
                }

                const { filteredOptions, highlightedId } = context;
                const {
                    value: { listElementRef, height },
                } = event;
                let highlightedOptionIndex = null;
                let optionAboveIndex = null;
                let optionAbove = null;

                for (let i = 0; i < filteredOptions.length; i += 1) {
                    const option = filteredOptions[i];

                    if (option.id === highlightedId) {
                        highlightedOptionIndex = i;
                        optionAboveIndex = i - 1;
                        optionAbove =
                            filteredOptions?.[optionAboveIndex] ?? null;
                        break;
                    }
                }

                // If:
                // 1. there is a currently highlighted option, and
                // 2. there is an option above it, then:
                if (
                    highlightedOptionIndex !== null &&
                    highlightedOptionIndex > 0 &&
                    optionAbove !== null
                ) {
                    // A. scroll to the option above if necessary
                    if (
                        listElementRef.current &&
                        optionAbove !== null &&
                        optionAboveIndex !== null
                    ) {
                        let scrollWindowStartIndex = 0;

                        if (listElementRef.current.scrollTop > 0) {
                            scrollWindowStartIndex = Math.floor(
                                listElementRef.current.scrollTop / height,
                            );
                        }
                        const needToScroll =
                            optionAboveIndex <= scrollWindowStartIndex - 1;

                        if (needToScroll) {
                            listElementRef.current.scrollTo({
                                left: 0,
                                top: optionAboveIndex * height,
                                behavior: 'auto',
                            });
                        }
                    }

                    // B. highlight the option above
                    return {
                        ...context,
                        highlightedId: optionAbove.id,
                    };
                }

                return context;
            }),
            arrowDown: assign((context, event): DropdownInputContext => {
                if (event.type !== 'ARROW_DOWN') {
                    return context;
                }

                const { filteredOptions, highlightedId } = context;
                const {
                    value: { listElementRef, height, maxListDisplayCount },
                } = event;
                let highlightedOptionIndex = null;
                let optionBelowIndex = null;
                let optionBelow = null;

                for (let i = 0; i < filteredOptions.length; i += 1) {
                    const option = filteredOptions[i];

                    if (option.id === highlightedId) {
                        highlightedOptionIndex = i;
                        optionBelowIndex = i + 1;
                        optionBelow =
                            filteredOptions?.[optionBelowIndex] ?? null;

                        break;
                    }
                }

                // If:
                // 1. there is currently a highlighted option, and
                // 2. there is an option below, then:
                if (
                    highlightedOptionIndex !== null &&
                    highlightedOptionIndex < filteredOptions.length - 1 &&
                    optionBelow !== null
                ) {
                    // A. scroll to the option below if necessary
                    if (
                        listElementRef.current &&
                        optionBelow !== null &&
                        optionBelowIndex !== null
                    ) {
                        let scrollWindowStartIndex = 0;

                        if (listElementRef.current.scrollTop > 0) {
                            scrollWindowStartIndex = Math.floor(
                                listElementRef.current.scrollTop / height,
                            );
                        }
                        const needToScroll =
                            optionBelowIndex - scrollWindowStartIndex >=
                            maxListDisplayCount;

                        if (needToScroll) {
                            listElementRef.current.scrollTo({
                                left: 0,
                                top:
                                    optionBelowIndex * height -
                                    (maxListDisplayCount - 1) * height,
                                behavior: 'auto',
                            });
                        }
                    }

                    // B. highlight the option below
                    return {
                        ...context,
                        highlightedId: optionBelow.id,
                    };
                }

                // If:
                // 1. there is not currently a highlighted option, and
                // 2. there is at least one option available, then:
                if (
                    highlightedOptionIndex === null &&
                    filteredOptions.length > 0
                ) {
                    // highlight the first option
                    return {
                        ...context,
                        highlightedId: filteredOptions[0].id,
                    };
                }

                return context;
            }),
        },
    },
);
